import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

// Sections - extracted into individual component files
import { AnimatedSection } from '@/components/sections/AnimatedSection';
import { HeroSection } from '@/components/sections/HeroSection';
import { AboutSection } from '@/components/sections/AboutSection';
import { GallerySection } from '@/components/sections/GallerySection';
import { VideoGallerySection } from '@/components/sections/VideoGallerySection';
import { NewsSection } from '@/components/sections/NewsSection';
import { EventsSection } from '@/components/sections/EventsSection';
import { RetreatsSection } from '@/components/sections/RetreatsSection';
import { TimetableSection } from '@/components/sections/TimetableSection';
import { PressSection } from '@/components/sections/PressSection';
import { MailingListSection } from '@/components/sections/MailingListSection';
import { ContactSection } from '@/components/sections/ContactSection';
import { ServicesSection } from '@/components/sections/ServicesSection';
import { TeamSection } from '@/components/sections/TeamSection';
import { FAQSection } from '@/components/sections/FAQSection';
import { TestimonialsSection } from '@/components/sections/TestimonialsSection';
import { SocialLinksSection } from '@/components/sections/SocialLinksSection';
import { MusicSection } from '@/components/sections/MusicSection';
import { Footer } from '@/components/sections/Footer';


export default function PerformerSite({ customSiteData }) {
  const { slug } = useParams();
  const [site, setSite] = useState(customSiteData || null);
  const [isLoading, setIsLoading] = useState(!customSiteData);
  const [isPageLoaded, setIsPageLoaded] = useState(!!customSiteData);
  const [error, setError] = useState(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  // Apply per-site SEO to the document head
  useEffect(() => {
    if (!site) return;
    const seo = site.seo || {};
    document.title = seo.title || `${site.performer_name}${site.tagline ? ` — ${site.tagline}` : ''}`;
    const setMeta = (attr, key, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    const desc = seo.description || site.tagline || '';
    setMeta('name', 'description', desc);
    setMeta('property', 'og:title', seo.title || site.performer_name);
    setMeta('property', 'og:description', desc);
    setMeta('property', 'og:image', seo.og_image || site.modules?.home?.background_image || '');
    return () => { document.title = 'Boxit Websites'; };
  }, [site]);

  useEffect(() => {
    // If we already have data from custom domain lookup, skip fetching
    if (customSiteData) {
      setTimeout(() => setIsPageLoaded(true), 100);
      return;
    }
    if (!slug) return;

    const fetchSite = async () => {
      // Check if this is a preview request (has ?preview=true or has auth token)
      const urlParams = new URLSearchParams(window.location.search);
      const isPreview = urlParams.get('preview') === 'true';
      
      try {
        let response;
        if (isPreview) {
          // Try authenticated preview endpoint for drafts
          try {
            response = await api.get(`/preview/sites/${slug}`);
            setIsPreviewMode(true);
          } catch (previewErr) {
            // If preview fails (no auth), fall back to public
            response = await api.get(`/public/sites/${slug}`);
          }
        } else {
          // Normal public access
          response = await api.get(`/public/sites/${slug}`);
        }
        setSite(response.data);
        // Trigger page load animation after data is ready
        setTimeout(() => setIsPageLoaded(true), 100);
      } catch (err) {
        setError(err.response?.status === 404 ? 'Site not found' : 'Failed to load site');
      } finally {
        setIsLoading(false);
      }
    };
    fetchSite();
  }, [slug, customSiteData]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/50 text-sm animate-pulse">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !site) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">404</h1>
          <p className="text-slate-400 mb-8">{error || 'Site not found'}</p>
          <Button onClick={() => window.location.href = '/'} className="bg-orange-500 hover:bg-orange-400">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  const themeStyle = site.theme?.style || 'dark_stage';
  const primaryColor = site.theme?.primary_color || '#F06415';
  const modules = site.modules || {};
  const enabledModules = site.enabled_modules || [];

  const DEFAULT_ORDER = [
    'about', 'retreats', 'press', 'gallery', 'video_gallery', 'news', 'music',
    'services', 'timetable', 'team', 'testimonials', 'faq', 'events', 'contact',
    'mailing_list', 'social_links'
  ];
  // Respect per-site module_order if set; unknown modules fall back to default position
  const customOrder = Array.isArray(site.module_order) && site.module_order.length > 0
    ? [...site.module_order.filter(m => m !== 'home'), ...DEFAULT_ORDER.filter(m => !site.module_order.includes(m))]
    : DEFAULT_ORDER;

  const SECTION_COMPONENTS = {
    about: <AboutSection key="about" data={modules.about} theme={themeStyle} primaryColor={primaryColor} />,
    press: <PressSection key="press" data={modules.press} theme={themeStyle} primaryColor={primaryColor} />,
    gallery: <GallerySection key="gallery" data={modules.gallery} theme={themeStyle} primaryColor={primaryColor} />,
    video_gallery: <VideoGallerySection key="video_gallery" data={modules.video_gallery} theme={themeStyle} primaryColor={primaryColor} />,
    news: <NewsSection key="news" data={modules.news} theme={themeStyle} primaryColor={primaryColor} />,
    music: <MusicSection key="music" data={modules.music} theme={themeStyle} primaryColor={primaryColor} />,
    services: <ServicesSection key="services" data={modules.services} theme={themeStyle} primaryColor={primaryColor} />,
    timetable: <TimetableSection key="timetable" data={modules.timetable} theme={themeStyle} primaryColor={primaryColor} />,
    team: <TeamSection key="team" data={modules.team} theme={themeStyle} primaryColor={primaryColor} />,
    testimonials: <TestimonialsSection key="testimonials" data={modules.testimonials} theme={themeStyle} primaryColor={primaryColor} />,
    faq: <FAQSection key="faq" data={modules.faq} theme={themeStyle} primaryColor={primaryColor} />,
    events: <EventsSection key="events" data={modules.events} theme={themeStyle} primaryColor={primaryColor} />,
    retreats: <RetreatsSection key="retreats" data={modules.retreats} theme={themeStyle} primaryColor={primaryColor} siteId={site.id} />,
    contact: <ContactSection key="contact" data={modules.contact} theme={themeStyle} primaryColor={primaryColor} siteId={site.id} />,
    mailing_list: <MailingListSection key="mailing_list" data={modules.mailing_list} theme={themeStyle} primaryColor={primaryColor} siteId={site.id} />,
    social_links: <SocialLinksSection key="social_links" data={modules.social_links} theme={themeStyle} primaryColor={primaryColor} />,
  };

  return (
    <div className="min-h-screen">
      {/* Preview Mode Banner */}
      {isPreviewMode && site.status === 'draft' && (
        <div className="bg-amber-500 text-amber-900 py-2 px-4 text-center text-sm font-medium sticky top-0 z-50">
          <span className="inline-flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-amber-900 animate-pulse"></span>
            Preview Mode - This site is not published yet
          </span>
        </div>
      )}
      
      {/* Home/Hero - Always show if enabled */}
      {enabledModules.includes('home') && (
        <HeroSection 
          data={modules.home} 
          theme={themeStyle}
          performerName={site.performer_name}
          tagline={site.tagline}
          primaryColor={primaryColor}
          isLoaded={isPageLoaded}
        />
      )}

      {customOrder.map(key => enabledModules.includes(key) ? SECTION_COMPONENTS[key] : null)}

      {/* Footer */}
      <Footer performerName={site.performer_name} theme={themeStyle} />
    </div>
  );
}
